import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Stack,
  Switch,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { useNavigate } from "react-router-dom";
import { usePageContext } from "../../context/PageContext";
import { useAgents } from "../../hooks/useAgents";
import { useEffectivePermissions, toSet, can } from "../../hooks/useEffectivePermissions";
import GenericTable from "../../components/GenericTable";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import { useTranslation } from "react-i18next";

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("pt-BR");
}

const AgentsList = () => {
  const { t } = useTranslation("settings");
  const navigate = useNavigate();
  const { currentPage, setCurrentPage } = usePageContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [errorMessage, setErrorMessage] = useState(null);

  const { data: permData } = useEffectivePermissions();
  const scopeSet = useMemo(() => toSet(permData?.scopes), [permData?.scopes]);
  const canCreate = can(scopeSet, "agents", "create");
  const canUpdate = can(scopeSet, "agents", "update");
  const canDelete = can(scopeSet, "agents", "delete");

  const {
    data,
    error,
    refetch,
    updateMutation,
    deleteMutation,
    isLoading,
    isFetching,
  } = useAgents(currentPage, pageSize, search);

  const agents = useMemo(() => data?.agents ?? [], [data?.agents]);
  const pagination = data?.pagination ?? {};
  const total = pagination?.total ?? agents.length;

  useEffect(() => {
    if (error) {
      setErrorMessage(error?.message || t("agentsListPage.loadError"));
    } else {
      setErrorMessage(null);
    }
  }, [error, t]);

  const skillLabels = useMemo(
    () => ({
      receptionist: t("agentsListPage.skillReceptionist"),
      faq: t("agentsListPage.skillFaq"),
      ticket_seller: t("agentsListPage.skillTicketSeller"),
    }),
    [t]
  );

  const rows = useMemo(
    () =>
      agents.map((a) => {
        const statusVal = a.status ?? a.enabled ?? a.is_active ?? a.isActive;
        const isActive = statusVal === "active" || statusVal === true;
        const skillKeys = Array.isArray(a.skills)
          ? a.skills.map((s) => (typeof s === "object" ? s.skill_key : s)).filter(Boolean)
          : typeof a.skills === "object"
            ? Object.keys(a.skills || {}).filter((k) => a.skills[k]?.enabled)
            : [];
        const skillsDisplay = skillKeys.map((k) => skillLabels[k] || k).join(", ") || "—";
        return {
          id: a.id,
          name: a.name ?? a.nome ?? "—",
          status: isActive,
          model: a.model ?? a.modelo ?? "—",
          skills: skillsDisplay,
          updatedAt: a.updatedAt ?? a.updated_at ?? a.data_ultima_alteracao,
        };
      }),
    [agents, skillLabels]
  );

  const handleToggleStatus = useCallback(
    (row) => {
      const nextStatus = row.status ? "inactive" : "active";
      updateMutation.mutate(
        { id: row.id, data: { status: nextStatus } },
        {
          onSuccess: () => {
            setSnack({ open: true, severity: "success", message: t("agentsListPage.snackStatusUpdated") });
            refetch();
          },
          onError: (err) => {
            setSnack({
              open: true,
              severity: "error",
              message: err?.message || t("agentsListPage.snackStatusError"),
            });
          },
        }
      );
    },
    [updateMutation, refetch, t]
  );

  const columns = useMemo(
    () => [
      { field: "name", headerName: t("shared.name"), flex: 1, minWidth: 180 },
      {
        field: "status",
        headerName: t("shared.status"),
        width: 160,
        renderCell: (params) => (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Switch
              size="small"
              checked={!!params.row.status}
              disabled={!canUpdate}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                e.stopPropagation();
                handleToggleStatus(params.row);
              }}
            />
            <Chip
              size="small"
              label={params.row.status ? t("shared.active") : t("shared.inactive")}
              color={params.row.status ? "success" : "default"}
              variant="outlined"
            />
          </Stack>
        ),
      },
      {
        field: "updatedAt",
        headerName: t("agentsListPage.columnLastUpdate"),
        width: 180,
        renderCell: (p) => formatDate(p.row.updatedAt),
      },
      {
        field: "actions",
        headerName: t("shared.actions"),
        width: 140,
        renderCell: (p) => (
          <Box display="flex" gap={0.5}>
            {canUpdate && (
              <Tooltip title={t("shared.edit")}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/agents/${p.row.id}`);
                  }}
                >
                  <EditRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={t("agentsListPage.analytics")}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/agents/${p.row.id}/analytics`);
                }}
              >
                <BarChartRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canDelete && (
              <Tooltip title={t("shared.delete")}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmDelete(p.row.id);
                  }}
                >
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    ],
    [t, canUpdate, canDelete, navigate, handleToggleStatus]
  );

  const handlePageChange = (page) => setCurrentPage(page + 1);

  const handleConfirmDelete = () => {
    if (!confirmDelete) return;
    deleteMutation.mutate(confirmDelete, {
      onSuccess: () => {
        setSnack({ open: true, severity: "success", message: t("agentsListPage.snackAgentDeleted") });
        setConfirmDelete(null);
        refetch();
      },
      onError: (err) => {
        setSnack({
          open: true,
          severity: "error",
          message: err?.message || t("agentsListPage.snackAgentDeleteError"),
        });
      },
    });
  };

  const handleDeleteSelected = async (ids) => {
    if (!ids?.length) return;
    const results = await Promise.allSettled(ids.map((id) => deleteMutation.mutateAsync(id)));
    const failed = results.filter((r) => r.status === "rejected");
    if (failed.length > 0) {
      setErrorMessage(
        failed.length === ids.length
          ? t("agentsListPage.snackBulkDeleteAllError")
          : t("agentsListPage.snackBulkDeletePartial", { failed: failed.length, total: ids.length })
      );
      setSnack({
        open: true,
        severity: "error",
        message:
          failed.length === ids.length
            ? t("agentsListPage.snackDeleteFailed")
            : t("agentsListPage.snackSomeDeletesFailed"),
      });
    } else {
      setSnack({ open: true, severity: "success", message: t("agentsListPage.snackBulkSuccess") });
    }
    refetch();
  };

  return (
    <Box className="tour-agents-root" sx={{ paddingTop: 1 }}>
      <Box
        className="tour-agents-header"
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          mb: 2,
          gap: isMobile ? 2 : 1,
        }}
      >
        <Typography variant="h4" gutterBottom>
          {t("agentsListPage.heading")}
        </Typography>
        {canCreate && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/agents/new")}
            fullWidth={isMobile}
          >
            {t("agentsListPage.createAiAgent")}
          </Button>
        )}
      </Box>

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      <Paper className="tour-agents-table">
        <GenericTable
          rows={rows}
          columns={columns}
          rowCount={total}
          page={currentPage - 1}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={setPageSize}
          onSearch={(term) => {
            setSearch(term);
            setCurrentPage(1);
            refetch();
          }}
          isLoading={isLoading || isFetching}
          onExportPDFTitle={t("agentsListPage.exportSelectedTitle")}
          exportFileName="agentes-de-ia"
          onDeleteSelected={canDelete ? handleDeleteSelected : undefined}
          onRowClick={(row) => navigate(`/agents/${row.id}`)}
        />
      </Paper>

      <ConfirmDialog
        open={!!confirmDelete}
        title={t("shared.deleteConfirmation")}
        content={
          deleteMutation.isLoading ? t("shared.processingDelete") : t("agentsListPage.deleteQuestion")
        }
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleConfirmDelete}
        confirmText={t("shared.delete")}
        confirmColor="error"
        loading={deleteMutation.isLoading}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        message={snack.message}
      />
    </Box>
  );
};

export default AgentsList;
